import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ElementRef,
  HostListener,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Observable, Subject, forkJoin, interval } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { NgbModal, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import moment from 'moment';
import { RestClientService } from '@core/service/rest-client.service';
import { AuthService } from '@core/service/auth.service';
import { UtilitiesService } from '@core/service/utilities.service';
import { ModalCreateReservationComponent } from 'app/accueil/reservation/modal-create-reservation/modal-create-reservation.component';
import { ModalHebergementDirectComponent } from 'app/accueil/hebergement/modal-hebergement-direct/modal-hebergement-direct.component';
import { ModalHebergementReservationComponent } from 'app/accueil/hebergement/modal-hebergement-reservation/modal-hebergement-reservation.component';
import { ActionSejour, SejourActionsService } from 'app/planning/sejour-actions.service';
import { FormAddReservationEspaceComponent } from 'app/location-espace/reservation-espace/form-add-reservation-espace/form-add-reservation-espace.component';
import { ModalOccupationEspaceComponent } from 'app/location-espace/occupation-espace/modal-occupation-espace/modal-occupation-espace.component';

/** « tout » : chambres de toutes les formules et espaces dans un même planning. */
export type CalendrierMode = 'chambre' | 'espace' | 'tout';

export interface CellInfo {
  statut: string; // 'L' | 'R' | 'C' | 'O' | 'M'
  client?: string;
  debut?: string;
  fin?: string;
  service?: string;
  motif?: string;
  statut_maintenance?: string;
  type?: string;
  count?: number; // nombre de réservations concurrentes (statut R)
  service_codification?: string;
  reservation_id?: number;
  hebergement_id?: number;
}

export interface CalendrierItem {
  id: number;
  libelle: string;
  code: string;
  type_chambre_libelle?: string;
  disponibilite: CellInfo[];
  /** Positionné en mode « tout » pour distinguer les espaces des chambres. */
  estEspace?: boolean;
}

interface FormuleHebergement {
  code: string;
  libelle: string;
  droit: string;
}

/** Formules d'hébergement immédiat, avec le droit d'accès à l'écran de caisse correspondant. */
const FORMULES_HEBERGEMENT: FormuleHebergement[] = [
  { code: 'PASS', libelle: 'Pass', droit: 'SERVICE_PASS' },
  { code: 'NUIT', libelle: 'Nuitée', droit: 'SERVICE_NUITE' },
  { code: 'JOURNEE', libelle: 'Journée', droit: 'SERVICE_JOURNEE' },
  { code: 'SEJOUR', libelle: 'Séjour', droit: 'SERVICE_SEJOUR' },
];

/** Un espace s'occupe sans formule : une seule entrée, soumise au droit d'occupation. */
const OCCUPATION_ESPACE: FormuleHebergement = { code: 'ESPACE', libelle: 'Occupation', droit: 'OCCUPATION_ESPACE' };

/** Jours consécutifs d'un même séjour (ou d'une même maintenance), affichés en une barre. */
export interface SegmentPlanning {
  debut: number;
  span: number;
  statut: string;
  cell: CellInfo;
  jour: string;
}

interface LignePlanning {
  item: CalendrierItem;
  segments: SegmentPlanning[];
}

interface GroupePlanning {
  libelle: string;
  lignes: LignePlanning[];
}

/** Statuts qui rendent la chambre indisponible à la vente. */
const STATUTS_PRIS = ['O', 'C', 'R'];

@Component({
  selector: 'app-disponibilite-calendrier',
  imports: [CommonModule, FormsModule, NgbModule],
  templateUrl: './disponibilite-calendrier.component.html',
  styleUrl: './disponibilite-calendrier.component.scss',
})
export class DisponibiliteCalendrierComponent implements OnInit, OnDestroy, OnChanges {
  @Input() mode: CalendrierMode = 'chambre';
  @Input() service_codification?: string;
  /** Actualisation automatique en secondes (0 : désactivée). */
  @Input() actualisationAuto = 0;
  /** Clic sur une case libre : proposer de réserver ou d'héberger (page Planning). */
  @Input() creation = false;

  @ViewChild('detailModal') detailModal!: TemplateRef<any>;
  @ViewChild('racine', { static: true }) racine!: ElementRef<HTMLElement>;

  private destroy$ = new Subject<void>();

  user: any;
  loading = false;
  jours: string[] = [];
  items: CalendrierItem[] = [];
  groupes: GroupePlanning[] = [];
  derniereMiseAJour?: Date;
  /** Formules (services) de l'hôtel, pour leurs plages horaires d'ouverture. */
  private services: any[] = [];
  /** Actions sur le séjour affiché dans l'infobulle (page Planning). */
  actionsSejour: { chargement: boolean; actions: ActionSejour[]; entreePossible: boolean; motifEntree?: string } = {
    chargement: false, actions: [], entreePossible: false,
  };
  menuCase: { visible: boolean; x: number; y: number; dessus?: boolean; item?: CalendrierItem; jour?: string;
    peutReserver: boolean; formules: FormuleHebergement[] } = { visible: false, x: 0, y: 0, peutReserver: false, formules: [] };
  occupationParJour: number[] = [];

  tt: { visible: boolean; x: number; y: number; dessus?: boolean; span?: number; cell?: CellInfo; item?: CalendrierItem; jour?: string } = {
    visible: false, x: 0, y: 0,
  };

  detail: { loading: boolean; items: any[]; chambre?: string; jour?: string; item?: CalendrierItem; jourIso?: string } = {
    loading: false, items: [],
  };

  debut: string = '';
  fin: string = '';

  // Légende
  readonly STATUTS = [
    { code: 'L', label: 'Libre',                         cssClass: 'statut-libre' },
    { code: 'R', label: 'Réservé (sans règlement)',       cssClass: 'statut-reserve' },
    { code: 'C', label: 'Confirmé (avec règlement)',      cssClass: 'statut-confirme' },
    { code: 'O', label: 'Occupé (hébergement)',           cssClass: 'statut-occupe' },
    { code: 'M', label: 'Maintenance bloquée',            cssClass: 'statut-maintenance' },
  ];

  constructor(
    private restClient: RestClientService,
    private authService: AuthService,
    private utilities: UtilitiesService,
    private modalService: NgbModal,
    private sejourActions: SejourActionsService,
  ) {
    this.user = this.authService.getCurrentUser();
    this._initDates();
  }

  private _initDates(): void {
    const today = new Date();
    const in14 = new Date(today);
    in14.setDate(today.getDate() + 13);
    this.debut = this._toIsoDate(today);
    this.fin   = this._toIsoDate(in14);
  }

  ngOnInit(): void {
    this.load();
    if (this.creation) this.chargerServices();
    if (this.actualisationAuto > 0) {
      interval(this.actualisationAuto * 1000)
        .pipe(takeUntil(this.destroy$))
        // Inutile d'interroger le serveur pour un onglet que personne ne regarde.
        .subscribe(() => !document.hidden && !this.loading && this.load(true));
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes['mode'] && !changes['mode'].firstChange) ||
      (changes['service_codification'] && !changes['service_codification'].firstChange)
    ) {
      this.load();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** @param silencieux actualisation de fond : pas d'indicateur de chargement ni de message d'erreur. */
  load(silencieux = false): void {
    if (!this.debut || !this.fin) return;

    if (!silencieux) this.loading = true;
    const payload: any = {
      user_id:   this.user.id,
      hotel_id:  this.user.hotel_id,
      groupe_id: this.user.groupe_id,
      debut:     this.debut,
      fin:       this.fin,
    };
    if (this.mode === 'chambre' && this.service_codification) {
      payload.service_codification = this.service_codification;
    }

    this.requeteCalendrier(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.loading = false;
          if (res && !res.hasError) {
            this.jours = res.jours || [];
            this.items = res.items || [];
            this.derniereMiseAJour = new Date();
            this.construirePlanning();
          } else if (!silencieux) {
            this.utilities.showNotification(
              'snackbar-danger',
              res?.status?.message || 'Erreur lors du chargement du calendrier',
              'bottom',
              'center'
            );
          }
        },
        error: () => {
          this.loading = false;
          if (!silencieux) {
            this.utilities.showNotification('snackbar-danger', 'Erreur de connexion', 'bottom', 'center');
          }
        },
      });
  }

  private requeteCalendrier(payload: any): Observable<any> {
    if (this.mode === 'chambre') return this.restClient.post('chambre/getCalendrierDisponibilite', payload);
    if (this.mode === 'espace') return this.restClient.post('espace/getCalendrierDisponibilite', payload);

    // Mode « tout » : les espaces forment un groupe à part, après les types de chambre.
    return forkJoin([
      this.restClient.post('chambre/getCalendrierDisponibilite', payload),
      this.restClient.post('espace/getCalendrierDisponibilite', payload),
    ]).pipe(
      map(([chambres, espaces]: any[]) => {
        if (chambres?.hasError) return chambres;
        if (espaces?.hasError) return espaces;
        return {
          hasError: false,
          jours: chambres?.jours || espaces?.jours || [],
          items: [
            ...(chambres?.items || []),
            ...(espaces?.items || []).map((espace: CalendrierItem) => ({
              ...espace,
              type_chambre_libelle: 'Espaces',
              estEspace: true,
            })),
          ],
        };
      })
    );
  }

  /** Décale la période affichée d'un nombre de jours (navigation semaine par semaine). */
  decaler(nbJours: number): void {
    const decale = (iso: string) => {
      const d = new Date(iso + 'T12:00:00');
      d.setDate(d.getDate() + nbJours);
      return this._toIsoDate(d);
    };
    this.debut = decale(this.debut);
    this.fin = decale(this.fin);
    this.load();
  }

  allerAujourdhui(): void {
    this._initDates();
    this.load();
  }

  get tauxRemplissage(): number {
    const disponibles = this.items.length * this.jours.length - this.totalMaintenance;
    const pris = this.totalOccupe + this.totalConfirme + this.totalReserve;
    return disponibles > 0 ? Math.round((pris * 100) / disponibles) : 0;
  }

  /** « FENDER LIONEL » devient « Fender L. » : la barre reste lisible même courte. */
  libelleCourt(cell: CellInfo): string {
    if (cell.statut === 'M') return cell.motif || 'Maintenance';
    const mots = (cell.client || '').trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (!mots.length) return this.getStatutLabel(cell.statut);
    const capital = (m: string) => m.charAt(0).toUpperCase() + m.slice(1);
    const nom = capital(mots[0]);
    const suite = mots.length > 1 ? ` ${mots[1].charAt(0).toUpperCase()}.` : '';
    const concurrentes = (cell.count ?? 1) > 1 ? ` +${(cell.count ?? 1) - 1}` : '';
    return nom + suite + concurrentes;
  }

  private construirePlanning(): void {
    const cle = (c: CellInfo) => [c.statut, c.client, c.debut, c.fin, c.count, c.motif].join('|');

    const lignes: LignePlanning[] = this.items.map((item) => {
      const segments: SegmentPlanning[] = [];
      item.disponibilite.forEach((cell, i) => {
        const precedent = segments[segments.length - 1];
        const prolonge =
          cell.statut !== 'L' &&
          precedent &&
          precedent.statut !== 'L' &&
          precedent.debut + precedent.span === i &&
          cle(precedent.cell) === cle(cell);
        if (prolonge) {
          precedent.span++;
        } else {
          segments.push({ debut: i, span: 1, statut: cell.statut, cell, jour: this.jours[i] });
        }
      });
      return { item, segments };
    });

    const parType = new Map<string, LignePlanning[]>();
    for (const ligne of lignes) {
      const type = ligne.item.type_chambre_libelle || '';
      parType.set(type, [...(parType.get(type) ?? []), ligne]);
    }
    this.groupes = [...parType.entries()].map(([libelle, l]) => ({ libelle, lignes: l }));

    this.occupationParJour = this.jours.map(
      (_, i) => this.items.filter((it) => STATUTS_PRIS.includes(it.disponibilite[i]?.statut)).length
    );
  }

  getStatutClass(statut: string): string {
    switch (statut) {
      case 'O': return 'statut-occupe';
      case 'C': return 'statut-confirme';
      case 'R': return 'statut-reserve';
      case 'M': return 'statut-maintenance';
      default:  return 'statut-libre';
    }
  }

  getStatutLabel(statut: string): string {
    switch (statut) {
      case 'O': return 'Occupé';
      case 'C': return 'Confirmé';
      case 'R': return 'Réservé';
      case 'M': return 'Maintenance';
      default:  return 'Libre';
    }
  }

  formatJour(dateStr: string): { day: string; weekday: string } {
    const d = new Date(dateStr + 'T12:00:00');
    return {
      day:     d.getDate().toString().padStart(2, '0'),
      weekday: d.toLocaleDateString('fr-FR', { weekday: 'short' }),
    };
  }

  isWeekend(dateStr: string): boolean {
    const d = new Date(dateStr + 'T12:00:00');
    return d.getDay() === 0 || d.getDay() === 6;
  }

  isToday(dateStr: string): boolean {
    return dateStr === this._toIsoDate(new Date());
  }

  private _toIsoDate(d: Date): string {
    return d.toISOString().split('T')[0];
  }

  get totalLibre(): number {
    return this.items.reduce((acc, item) => acc + item.disponibilite.filter(c => c.statut === 'L').length, 0);
  }

  get totalReserve(): number {
    return this.items.reduce((acc, item) => acc + item.disponibilite.filter(c => c.statut === 'R').length, 0);
  }

  get totalConfirme(): number {
    return this.items.reduce((acc, item) => acc + item.disponibilite.filter(c => c.statut === 'C').length, 0);
  }

  get totalOccupe(): number {
    return this.items.reduce((acc, item) => acc + item.disponibilite.filter(c => c.statut === 'O').length, 0);
  }

  get totalMaintenance(): number {
    return this.items.reduce((acc, item) => acc + item.disponibilite.filter(c => c.statut === 'M').length, 0);
  }

  onCellClick(event: MouseEvent, cell: CellInfo, item: CalendrierItem, jour: string | undefined, span = 1): void {
    if (cell.statut === 'L') return;

    // Cellule concurrente → mini modal
    if ((cell.count ?? 1) > 1) {
      this.hideTooltip();
      this.openDetailModal(item, jour);
      return;
    }

    // Cellule simple → tooltip
    event.stopPropagation();
    if (this.tt.visible && this.tt.cell === cell) { this.hideTooltip(); return; }
    this.tt = { visible: true, ...this.positionSous(event.currentTarget as HTMLElement), span, cell, item, jour };
    this.chargerActionsSejour(cell, item);
  }

  executerAction(action: ActionSejour): void {
    this.hideTooltip();
    action.executer().then(() => this.load());
  }

  private chargerActionsSejour(cell: CellInfo, item: CalendrierItem): void {
    this.actionsSejour = { chargement: false, actions: [], entreePossible: false };
    if (!this.creation) return;

    const formule = FORMULES_HEBERGEMENT.find((f) => f.code === cell.service_codification);
    const droitHebergement =
      !!formule && this.authService.hasAccess('SERVICE_CAISSE') && this.authService.hasAccess(formule.droit);
    const droitReservation = this.authService.hasAccess('ACCUEIL') && this.authService.hasAccess('RESERVATION');

    const espace = this.estEspace(item);
    let chargement$;
    if (espace) {
      if (cell.statut === 'O' && cell.hebergement_id && this.authService.hasAccess('OCCUPATION_ESPACE')) {
        chargement$ = this.sejourActions.chargerHebergement(cell.hebergement_id, 'ESPACE');
      } else if (['R', 'C'].includes(cell.statut) && cell.reservation_id && this.droitReservation(item)) {
        chargement$ = this.sejourActions.chargerReservation(cell.reservation_id, 'ESPACE');
      }
    } else if (cell.statut === 'O' && cell.hebergement_id && droitHebergement) {
      chargement$ = this.sejourActions.chargerHebergement(cell.hebergement_id);
    } else if (['R', 'C'].includes(cell.statut) && cell.reservation_id && droitReservation) {
      chargement$ = this.sejourActions.chargerReservation(cell.reservation_id);
    }
    if (!chargement$) return;

    this.actionsSejour.chargement = true;
    chargement$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (sejour) => {
        // L'utilisateur a pu cliquer sur une autre barre entre-temps.
        if (this.tt.cell !== cell) return;
        if (!sejour) {
          this.actionsSejour = { chargement: false, actions: [], entreePossible: false };
          return;
        }
        if (cell.statut === 'O') {
          this.actionsSejour = {
            chargement: false,
            actions: espace
              ? this.sejourActions.actionsOccupation(sejour)
              : this.sejourActions.actionsHebergement(sejour, cell.service_codification),
            entreePossible: false,
          };
          return;
        }
        const motifEntree = this.sejourActions.motifEntreeImpossible(sejour) ?? undefined;
        this.actionsSejour = {
          chargement: false,
          actions: espace ? this.sejourActions.actionsReservationEspace(sejour) : this.sejourActions.actionsReservation(sejour),
          entreePossible: !motifEntree,
          motifEntree,
        };
      },
      error: () => (this.actionsSejour = { chargement: false, actions: [], entreePossible: false }),
    });
  }

  @HostListener('document:keydown.escape')
  fermerAvecEchap(): void {
    if (this.tt.visible || this.menuCase.visible) this.hideTooltip();
  }

  /**
   * Position d'une bulle par rapport au planning (et non à l'écran) : elle reste collée
   * à la case cliquée quels que soient le défilement et les conteneurs de la page.
   * Elle s'ouvre au-dessus de la case quand la place manque en dessous.
   */
  private positionSous(cible: HTMLElement): { x: number; y: number; dessus: boolean } {
    const boite = this.racine.nativeElement.getBoundingClientRect();
    const rect = cible.getBoundingClientRect();
    // Au-dessus seulement si la place y est nettement plus grande qu'en dessous.
    const dessus = window.innerHeight - rect.bottom < 340 && rect.top > window.innerHeight - rect.bottom + 120;
    // Moitié de la largeur de la bulle, limitée sur un écran étroit (la bulle s'y adapte).
    const demiLargeur = Math.min(190, boite.width / 2);
    const centre = rect.left + rect.width / 2 - boite.left;
    return {
      x: Math.min(Math.max(centre, demiLargeur), boite.width - demiLargeur),
      y: dessus ? rect.top - boite.top - 6 : rect.bottom - boite.top + 6,
      dessus,
    };
  }

  hideTooltip(): void {
    this.tt = { ...this.tt, visible: false };
    this.menuCase = { ...this.menuCase, visible: false };
  }

  /** Une case libre est cliquable pour une chambre, à partir d'aujourd'hui. */
  caseCreable(item: CalendrierItem, jour: string): boolean {
    return this.creation && jour >= this._toIsoDate(new Date());
  }

  estEspace(item?: CalendrierItem): boolean {
    return this.mode === 'espace' || !!item?.estEspace;
  }

  private droitReservation(item?: CalendrierItem): boolean {
    return this.estEspace(item)
      ? this.authService.hasAccess('LOCATION_ESPACE') && this.authService.hasAccess('RESERVATION_ESPACE')
      : this.authService.hasAccess('ACCUEIL') && this.authService.hasAccess('RESERVATION');
  }

  ouvrirMenuCase(event: MouseEvent, item: CalendrierItem, jour: string): void {
    event.stopPropagation();
    this.hideTooltip();
    this.menuCase = {
      visible: true,
      ...this.positionSous(event.currentTarget as HTMLElement),
      item,
      jour,
      peutReserver: this.droitReservation(item),
      formules: this.formulesHebergement(item, jour),
    };
  }

  reserver(): void {
    this.reserverSur(this.menuCase.item, this.menuCase.jour);
  }

  peutReserver(item?: CalendrierItem, jour?: string): boolean {
    return !!item && !!jour && this.caseCreable(item, jour) && this.droitReservation(item);
  }

  reserverSur(item?: CalendrierItem, jour?: string): void {
    this.hideTooltip();
    this.modalService.dismissAll();
    if (!item || !jour) return;
    const debut = new Date(jour + 'T12:00:00');
    const espace = this.estEspace(item);
    const modalRef = this.modalService.open(espace ? FormAddReservationEspaceComponent : ModalCreateReservationComponent, {
      size: 'xl', backdrop: 'static', keyboard: false,
    });
    modalRef.componentInstance.data = {
      action: 'add',
      item: espace ? { debut_reservation: debut, espace_id: item.id } : { debut_reservation: debut, chambre_id: item.id },
    };
    modalRef.result.then(() => this.load(), () => {});
  }

  /** Une réservation devient un hébergement si l'utilisateur a accès à l'écran de caisse de sa formule. */
  peutFaireEntrer(cell?: CellInfo, item?: CalendrierItem): boolean {
    if (!this.creation || !cell?.reservation_id || !['R', 'C'].includes(cell.statut)) return false;
    if (this.estEspace(item)) {
      return this.authService.hasAccess('LOCATION_ESPACE') && this.authService.hasAccess('OCCUPATION_ESPACE');
    }
    const formule = FORMULES_HEBERGEMENT.find((f) => f.code === cell.service_codification);
    return !!formule && this.authService.hasAccess('SERVICE_CAISSE') && this.authService.hasAccess(formule.droit);
  }

  faireEntrer(cell?: CellInfo, item?: CalendrierItem): void {
    this.hideTooltip();
    if (!cell?.reservation_id) return;
    const espace = this.estEspace(item);
    const modalRef = this.modalService.open(espace ? ModalOccupationEspaceComponent : ModalHebergementReservationComponent, {
      size: 'xl', backdrop: 'static', keyboard: false,
    });
    modalRef.componentInstance.data = espace
      ? { action: 'add', reservation_id: cell.reservation_id }
      : { action: 'add', service_codification: cell.service_codification, reservation_id: cell.reservation_id };
    modalRef.result.then(() => this.load(), () => {});
  }

  /**
   * Formules d'hébergement immédiat proposées sur une case : la journée en cours
   * seulement (l'hébergement commence maintenant), selon les droits de caisse.
   * La disponibilité réelle de la chambre est contrôlée par le formulaire.
   */
  formulesHebergement(item?: CalendrierItem, jour?: string): FormuleHebergement[] {
    if (!item || !jour || !this.caseCreable(item, jour) || !this.isToday(jour)) return [];
    if (this.estEspace(item)) {
      return this.authService.hasAccess('LOCATION_ESPACE') && this.authService.hasAccess(OCCUPATION_ESPACE.droit)
        ? [OCCUPATION_ESPACE] : [];
    }
    if (!this.authService.hasAccess('SERVICE_CAISSE')) return [];
    return FORMULES_HEBERGEMENT.filter((formule) => this.authService.hasAccess(formule.droit));
  }

  private chargerServices(): void {
    this.restClient.post('service/getByCriteria', { data: {}, index: 0, size: 10000 })
      .pipe(takeUntil(this.destroy$))
      .subscribe({ next: (res: any) => (this.services = (!res?.hasError && res?.items) || []) });
  }

  /**
   * Plage horaire de la formule : même règle que la modale d'hébergement direct
   * (sans plage, la formule est toujours ouverte ; une plage peut passer minuit).
   */
  plageFormule(formule: FormuleHebergement): { ouverte: boolean; horaires?: string } {
    const service = this.services.find((sv) => sv.codification === formule.code);
    const debut = service?.periodicite_heure_debut;
    const fin = service?.periodicite_heure_fin;
    if (!debut || !fin) return { ouverte: true };
    const d = moment(debut, 'HH:mm:ss');
    const f = moment(fin, 'HH:mm:ss');
    if (f.isBefore(d)) f.add(1, 'day');
    const maintenant = moment();
    const ouverte = maintenant.isBetween(d, f) || maintenant.clone().add(1, 'day').isBetween(d, f);
    return { ouverte, horaires: `${d.format('HH:mm')} à ${f.format('HH:mm')}` };
  }

  /** La barre affichée couvre-t-elle la journée en cours ? */
  barreCouvreAujourdhui(): boolean {
    if (!this.tt.jour) return false;
    const debut = moment(this.tt.jour, 'YYYY-MM-DD');
    const fin = debut.clone().add(this.tt.span ?? 1, 'days');
    return moment().isSameOrAfter(debut, 'day') && moment().isBefore(fin, 'day');
  }

  /** L'hébergement commence maintenant : proposé depuis n'importe quelle barre de la chambre. */
  formulesHebergementChambre(item?: CalendrierItem): FormuleHebergement[] {
    return this.formulesHebergement(item, this._toIsoDate(new Date()));
  }

  heberger(formule: FormuleHebergement, item: CalendrierItem | undefined = this.menuCase.item): void {
    this.hideTooltip();
    this.modalService.dismissAll();
    if (!item) return;
    if (this.estEspace(item)) {
      const occupation = this.modalService.open(ModalOccupationEspaceComponent, {
        size: 'xl', backdrop: 'static', keyboard: false,
      });
      occupation.componentInstance.data = { action: 'add', espace_id: item.id };
      occupation.result.then(() => this.load(), () => {});
      return;
    }
    const modalRef = this.modalService.open(ModalHebergementDirectComponent, {
      size: 'xl', backdrop: 'static', centered: true,
    });
    modalRef.componentInstance.data = {
      action: 'add',
      type_service_codification: formule.code,
      chambre_id: item.id,
    };
    modalRef.result.then(() => this.load(), () => {});
  }

  openDetailModal(item: CalendrierItem, jour: string | undefined): void {
    if (!jour) return;
    this.detail = {
      loading: true,
      items: [],
      item,
      jourIso: jour,
      chambre: item.libelle,
      jour: `${this.formatJour(jour).weekday} ${this.formatJour(jour).day}`,
    };
    this.modalService.open(this.detailModal, { size: 'md', centered: true });
    this.restClient.post('reservation/getByCriteria', {
      user_id:   this.user.id,
      hotel_id:  this.user.hotel_id,
      groupe_id: this.user.groupe_id,
      data: { chambre_id: item.id },
      extra_control: [
        { key: 'debut_reservation', sign: '<', value: jour + 'T23:59:59' },
        { key: 'fin_reservation',   sign: '>', value: jour + 'T00:00:00' },
      ],
      index: 0,
      size: 100,
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res: any) => {
        this.detail.loading = false;
        this.detail.items = res?.items ?? [];
      },
      error: () => { this.detail.loading = false; },
    });
  }

  getCellClass(cell: CellInfo, jour: string | undefined): string {
    let cls = 'col-statut ' + this.getStatutClass(cell.statut);
    if (jour && this.isWeekend(jour)) cls += ' weekend';
    return cls;
  }

}
